import { create } from 'zustand';

export interface FormViewer {
  user_id: string;
  user_name: string;
  socket_id: string;
  joined_at: string;
}

export interface FormEditing {
  field_name: string;
  user_id: string;
  user_name: string;
  started_at: string;
}

export interface FormPermissions {
  canEdit: boolean;
  canSign: boolean;
  canApprove: boolean;
}

interface FormsState {
  // Connection state
  isConnected: boolean;

  // Current form being viewed
  activeFormId: string | null;

  // Viewers per form
  viewersByForm: Record<string, FormViewer[]>;

  // Editing indicators per form
  editingByForm: Record<string, FormEditing[]>;

  // User's permissions for current form
  permissions: FormPermissions | null;

  // Field updates from other users (for optimistic UI)
  pendingUpdates: Record<string, { field_name: string; value: unknown; updated_by: string }>;

  // Actions
  setConnected: (connected: boolean) => void;
  setActiveFormId: (formId: string | null) => void;

  // Viewers
  setViewers: (formId: string, viewers: FormViewer[]) => void;
  addViewer: (formId: string, viewer: FormViewer) => void;
  removeViewer: (formId: string, userId: string) => void;

  // Editing
  setEditing: (formId: string, fieldName: string, user: { id: string; name: string }) => void;
  clearEditing: (formId: string, fieldName: string) => void;
  clearAllEditingForUser: (formId: string, userId: string) => void;
  getEditingUser: (formId: string, fieldName: string) => FormEditing | undefined;

  // Permissions
  setPermissions: (permissions: FormPermissions) => void;

  // Pending updates
  addPendingUpdate: (formId: string, fieldName: string, value: unknown, updatedBy: string) => void;
  clearPendingUpdate: (formId: string, fieldName: string) => void;

  // Clear state
  clearFormState: (formId: string) => void;
  clearAllState: () => void;
}

export const useFormsStore = create<FormsState>((set, get) => ({
  // Initial state
  isConnected: false,
  activeFormId: null,
  viewersByForm: {},
  editingByForm: {},
  permissions: null,
  pendingUpdates: {},

  // Connection
  setConnected: (connected) => set({ isConnected: connected }),
  setActiveFormId: (formId) => set({ activeFormId: formId }),

  // Viewers
  setViewers: (formId, viewers) =>
    set((state) => ({
      viewersByForm: { ...state.viewersByForm, [formId]: viewers },
    })),

  addViewer: (formId, viewer) =>
    set((state) => {
      const current = state.viewersByForm[formId] || [];
      // Avoid duplicates
      if (current.some((v) => v.user_id === viewer.user_id)) {
        return state;
      }
      return {
        viewersByForm: {
          ...state.viewersByForm,
          [formId]: [...current, viewer],
        },
      };
    }),

  removeViewer: (formId, userId) =>
    set((state) => ({
      viewersByForm: {
        ...state.viewersByForm,
        [formId]: (state.viewersByForm[formId] || []).filter(
          (v) => v.user_id !== userId
        ),
      },
    })),

  // Editing
  setEditing: (formId, fieldName, user) =>
    set((state) => {
      const current = state.editingByForm[formId] || [];
      // Remove existing entry for this field if any
      const filtered = current.filter((e) => e.field_name !== fieldName);
      return {
        editingByForm: {
          ...state.editingByForm,
          [formId]: [
            ...filtered,
            {
              field_name: fieldName,
              user_id: user.id,
              user_name: user.name,
              started_at: new Date().toISOString(),
            },
          ],
        },
      };
    }),

  clearEditing: (formId, fieldName) =>
    set((state) => ({
      editingByForm: {
        ...state.editingByForm,
        [formId]: (state.editingByForm[formId] || []).filter(
          (e) => e.field_name !== fieldName
        ),
      },
    })),

  clearAllEditingForUser: (formId, userId) =>
    set((state) => ({
      editingByForm: {
        ...state.editingByForm,
        [formId]: (state.editingByForm[formId] || []).filter(
          (e) => e.user_id !== userId
        ),
      },
    })),

  getEditingUser: (formId, fieldName) => {
    const editing = get().editingByForm[formId] || [];
    return editing.find((e) => e.field_name === fieldName);
  },

  // Permissions
  setPermissions: (permissions) => set({ permissions }),

  // Pending updates
  addPendingUpdate: (formId, fieldName, value, updatedBy) =>
    set((state) => ({
      pendingUpdates: {
        ...state.pendingUpdates,
        [`${formId}:${fieldName}`]: { field_name: fieldName, value, updated_by: updatedBy },
      },
    })),

  clearPendingUpdate: (formId, fieldName) =>
    set((state) => {
      const { [`${formId}:${fieldName}`]: _, ...rest } = state.pendingUpdates;
      return { pendingUpdates: rest };
    }),

  // Clear
  clearFormState: (formId) =>
    set((state) => {
      const { [formId]: _viewers, ...restViewers } = state.viewersByForm;
      const { [formId]: _editing, ...restEditing } = state.editingByForm;
      // Clear pending updates for this form
      const pendingUpdates = { ...state.pendingUpdates };
      Object.keys(pendingUpdates).forEach((key) => {
        if (key.startsWith(`${formId}:`)) {
          delete pendingUpdates[key];
        }
      });
      return {
        viewersByForm: restViewers,
        editingByForm: restEditing,
        pendingUpdates,
        permissions: state.activeFormId === formId ? null : state.permissions,
      };
    }),

  clearAllState: () =>
    set({
      isConnected: false,
      activeFormId: null,
      viewersByForm: {},
      editingByForm: {},
      permissions: null,
      pendingUpdates: {},
    }),
}));
